import http from '../../axios';
import {
    AvatarDto,
    BankDto,
    BankGift,
    BankOption,
    BonusStatusInfo,
    BoostersDto,
    BoosterType,
    HeroExchangeDetails,
    HeroExchangeOptions, MarketInfo, Monastery10Dto, MonasteryDto,
    OblivionHeroDto,
    OblivionTokenDto,
    PortalShopDto,
    PurchaseInfo,
    RewardedAdvType,
    RewardedInfo,
    RewardedResult,
    ShopArtifact,
    ShopArtifactContext,
    ShopCreature,
    ShopElephant,
    ShopRankDto,
    ShopState
} from "@/store/shop/types";
import {Player} from "@/store/player/types";
import PlayerStore from "@/store/player";
import {GameState} from "@/constants/game-state";

export default class ShopStore {

    private static state: ShopState = {
        artifactOptions: [],
        shopCreatures: [],
        bankOptions: [],
        bankGifts: [],
        boosters: undefined,
        bonusStatus: undefined,
        rewardedInfo: undefined
    };

    public static getBonusStatusInfo(): BonusStatusInfo | undefined {
        return ShopStore.state.bonusStatus;
    }

    public static getRewardedInfo(): RewardedInfo | undefined {
        return ShopStore.state.rewardedInfo;
    }

    public static getShopCreatures() {
        return ShopStore.state.shopCreatures;
    }

    public static getBankOptions() {
        return ShopStore.state.bankOptions;
    }

    public static getBankGifts() {
        return ShopStore.state.bankGifts;
    }

    public static getArtifactOptions() {
        return ShopStore.state.artifactOptions;
    }

    private static SET_SHOP_CREATURES(shopCreatures: ShopCreature[]) {
        ShopStore.state.shopCreatures = shopCreatures;
    }

    private static SET_BOOSTERS(boosters: BoostersDto) {
        ShopStore.state.boosters = boosters;
    }

    private static SET_BANK_OPTIONS(bankOptions: BankOption[], bankGifts: BankGift[], bonusStatusInfo: BonusStatusInfo, rewardedInfo: RewardedInfo) {
        ShopStore.state.bankOptions = bankOptions;
        ShopStore.state.bonusStatus = bonusStatusInfo;
        ShopStore.state.rewardedInfo = rewardedInfo;
        ShopStore.state.bankGifts = bankGifts;
    }

    private static SET_ARTIFACT_OPTIONS(artifactOptions: ShopArtifact[]) {
        ShopStore.state.artifactOptions = artifactOptions;
    }

    public static fetchGiftPurchaseOptions(): Promise<BankOption[]> {
        return http.get('/api/giftpurchase/gems/list')
            .then(r => r.data)
            .then(options => {
                var res: BankOption[] = [];
                for (const option of options) {
                    const opt = Object.assign(new BankOption(), option);
                    res.push(opt);
                }
                
                return res;
            })
    }

    public static reserveGiftPurchase(receiverId: number): Promise<void> {
        const params = new URLSearchParams();
        params.append('receiverId', receiverId.toString());

        return http.post('/api/giftpurchase/gems/reserve', params);
    }

    public static fetchBoosters(): Promise<BoostersDto> {
        return http.get('/api/shop/booster/list')
            .then(r => r.data)
            .then(obj => {
                let boo = Object.assign(new BoostersDto(), obj);
                ShopStore.SET_BOOSTERS(boo);
                return boo;
            });
    }

    public static fetchHeroTownShopCreatures(): Promise<void> {
        return http.get('/api/shop/creature/town/hero/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                ShopStore.SET_SHOP_CREATURES(res);
            });
    }

    public static fetchAmetistShopCreatures(): Promise<ShopCreature[]> {
        return http.get('/api/shop/creature/ametist/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchFractionGoodCreatures(): Promise<ShopCreature[]> {
        return http.get('/api/shop/creature/fraction/good/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchFractionEvilCreatures(): Promise<ShopCreature[]> {
        return http.get('/api/shop/creature/fraction/evil/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchWaterShopCreatures(): Promise<ShopCreature[]> {
        return http.get('/api/shop/creature/water/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchPortalShopCreatures(): Promise<PortalShopDto> {
        return http.get('/api/shop/creature/portal/list')
            .then(r => r.data)
            .then(obj => {
                return Object.assign(new PortalShopDto(), obj);
            });
    }

    public static fetchTitanTownShopCreatures(): Promise<void> {
        return http.get('/api/shop/creature/town/titan/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                ShopStore.SET_SHOP_CREATURES(res);
            });
    }

    public static fetchShopCreatures(): Promise<void> {
        return http.get('/api/shop/creature/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                ShopStore.SET_SHOP_CREATURES(res);
            });
    }

    public static fetchCreatureById(heroId: string): Promise<ShopCreature> {
        return http.get('/api/shop/creature/id/' + heroId)
            .then(r => r.data)
            .then(creatureObj => {
                return Object.assign(new ShopCreature(), creatureObj);
            });
    }

    public static fetchAnyCreatureById(heroId: string): Promise<ShopCreature> {
        return http.get('/api/shop/any/creature/id/' + heroId)
            .then(r => r.data)
            .then(creatureObj => {
                return Object.assign(new ShopCreature(), creatureObj);
            });
    }

    public static fetchHeroShop(tabId: string): Promise<void> {
        return http.get('/api/shop/creature/' + tabId + '/list')
            .then(r => r.data)
            .then(creaturesObj => {
                let res: ShopCreature[] = [];
                for (let obj of creaturesObj) {
                    let sc = Object.assign(new ShopCreature(), obj);
                    res.push(sc);
                }
                ShopStore.SET_SHOP_CREATURES(res);
            });
    }

    public static fetchAvatars(): Promise<AvatarDto[]> {
        return this.doFetchAvatars('/api/shop/avatar/list');
    }

    public static fetchAtlantisAvatars(): Promise<AvatarDto[]> {
        return this.doFetchAvatars('/api/shop/avatar/atlantis/list');
    }

    public static fetchShipAvatars(): Promise<AvatarDto[]> {
        return this.doFetchAvatars('/api/shop/avatar/ship/list');
    }

    public static fetchDiahlaAvatars(): Promise<AvatarDto[]> {
        return this.doFetchAvatars('/api/shop/avatar/diahla/list');
    }

    public static doFetchAvatars(endpoint: string): Promise<AvatarDto[]> {
        return http.get(endpoint)
            .then(r => r.data)
            .then(avatarsObj => {
                let res: AvatarDto[] = [];
                for (let obj of avatarsObj) {
                    let sc = Object.assign(new AvatarDto(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static buyAvatar(id: string): Promise<Player> {
        return http.post('/api/shop/avatar/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                GameState.REQUEST_REFRESH_NAV_PANEL = true;
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static fetchHeroExchangeOptions(tabId: string): Promise<HeroExchangeOptions> {
        return http.get('/api/shop/creature/exchange/' + tabId.toUpperCase() + '/options')
            .then(r => r.data)
            .then(obj => {
                return Object.assign(new HeroExchangeOptions(), obj);
            });
    }

    public static fetchHeroExchangeDetails(creatureId: string, sellIds: number[]): Promise<HeroExchangeDetails> {
        return http.post('/api/shop/creature/details/exchange/' + String(creatureId), sellIds)
            .then(r => r.data)
            .then(obj => {
                return Object.assign(new HeroExchangeDetails(), obj);
            });
    }

    public static fetchBankInfo(): Promise<void> {
        return http.get('/api/shop/bank/info')
            .then(r => r.data)
            .then(obj => {
                let bo = Object.assign(new BankDto(), obj);
                ShopStore.SET_BANK_OPTIONS(bo.bankOptions, bo.bankGifts, bo.bonusStatusInfo, bo.rewardedInfo);
            });
    }

    public static fetchShopItemOptions(context: ShopArtifactContext): Promise<void> {
        return http.get('/api/shop/artifact/list/context/' + String(context))
            .then(r => r.data)
            .then(itemObj => {
                let itemOptions: ShopArtifact[] = [];
                for (let obj of itemObj) {
                    let item = Object.assign(new ShopArtifact(), obj);
                    itemOptions.push(item);
                }
                ShopStore.SET_ARTIFACT_OPTIONS(itemOptions);
            })
    }

    public static buyCreature(id: string, marketName?: string): Promise<Player> {
        return http.post('/api/shop/creature/' + id + '/buy', null, {params: {marketName}})
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCreatureAmetist(id: string): Promise<Player> {
        return http.post('/api/shop/creature/ametist/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCreatureFraction(id: string): Promise<Player> {
        return http.post('/api/shop/creature/fraction/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCreatureToken(id: string): Promise<Player> {
        return http.post('/api/shop/creature/token/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCreaturePortal(id: string): Promise<Player> {
        return http.post('/api/shop/creature/portal/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static exchangeCreature(id: string, sellIds: number[]): Promise<Player> {
        return http.post('/api/shop/creature/' + id + '/exchange', sellIds)
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static getPurchaseInfo(): Promise<PurchaseInfo> {
        return http.get('/api/shop/bank/purchase/info')
            .then(r => r.data)
            .then(purchaseObj => {
                let pi: PurchaseInfo = Object.assign(new PurchaseInfo(), purchaseObj);
                return pi;
            });
    }

    public static fetchBonusStatusInfo(): Promise<BonusStatusInfo> {
        return http.get('/api/shop/bank/bonus/status/info')
            .then(r => r.data)
            .then(purchaseObj => {
                let pi: BonusStatusInfo = Object.assign(new BonusStatusInfo(), purchaseObj);
                return pi;
            });
    }

    public static takePaidGems(): Promise<Player> {
        return http.post('/api/shop/bank/gems/take')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static takeBankGift(id: number): Promise<Player> {
        return http.post('/api/shop/bank/gift/' + String(id) + '/take')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static takeBankFreeOption(id: string): Promise<Player> {
        return http.post('/api/shop/bank/free/' + id + '/take')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyValuteSocial(price: number): Promise<string> {
        return http.post('/api/payment/buy/' + String(price)).then(r => {
            return r.data;
        });
    }

    public static buyValuteXsolla(price: number): Promise<string> {
        return http.post('/api/xsolla/buy/' + String(price)).then(r => {
            return r.data;
        });
    }

    public static buyValuteXsollaIFrame(price: number): Promise<string> {
        return http.post('/api/xsolla/buy_iframe/' + String(price)).then(r => {
            return r.data;
        });
    }

    public static buyValuteRobokassa(price: number, title: string): Promise<string> {
        return http.post(`/api/robokassa/buy/${String(price)}/${title}`).then(r => {
            return r.data;
        });
    }

    public static buyValuteBeeline(price: number): Promise<string> {
        return http.post(`api/integration/beeline/buy/${String(price)}`).then(r => {
            return r.data;
        });
    }

    public static buyValuteStub(price: number): Promise<Player> {
        return http.get('/api/shop/bank/buy/' + String(price)).then(playerObj => {
            let player: Player = Object.assign(new Player(), playerObj);
            // PlayerStore.SET_PLAYER(player);
            return player;
        });
    }

    public static buyArtifact(artifactId: string, creatureIds: number[]): Promise<Player> {
        const params = new URLSearchParams();
        params.append('creatureId', creatureIds.join("|"))

        return http.post('/api/shop/artifact/' + artifactId + '/buy', params)
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyBooster(boosterType: BoosterType): Promise<BoostersDto> {
        return http.post('/api/shop/booster/' + boosterType + '/buy')
            .then(r => r.data)
            .then(obj => {
                let boostersDto: BoostersDto = Object.assign(new BoostersDto(), obj);
                ShopStore.SET_BOOSTERS(boostersDto);
                PlayerStore.SET_BALANCE(boostersDto.balance);
                return boostersDto;
            });
    }

    // public static fetchShopForces(creatureId: number): Promise<ShopForce[]> {
    //     return http.get('/api/shop/force/list?creatureId=' + String(creatureId))
    //         .then(r => r.data)
    //         .then(forcesObj => {
    //             let res: ShopForce[] = [];
    //             for (let obj of forcesObj) {
    //                 let sc = Object.assign(new ShopForce(), obj);
    //                 res.push(sc);
    //             }
    //             return res;
    //         });
    // }

    // public static buyForce(warriorClass: any, creatureId: any): Promise<Player> {
    //     const params = new URLSearchParams();
    //     params.append('creatureId', creatureId.toString())
    //
    //     return http.post('/api/shop/force/' + warriorClass + '/buy', params)
    //         .then(r => r.data)
    //         .then(playerObj => {
    //             let player: Player = Object.assign(new Player(), playerObj);
    //             PlayerStore.SET_PLAYER(player);
    //             return player;
    //         });
    // }

    public static startWatchRewarded(rewardedAdvType: RewardedAdvType): Promise<void> {
        return http.post('/api/adv/rewarded/watch/' + String(rewardedAdvType) + '/start');
    }

    public static takeGemsForRewardedAdv(): Promise<RewardedResult> {
        return http.post('/api/adv/rewarded/take/gems')
            .then(r => r.data)
            .then(obj => {
                let rewardedResult: RewardedResult = Object.assign(new RewardedResult(), obj);
                PlayerStore.SET_PLAYER(rewardedResult.player);
                return rewardedResult;
            });
    }

    public static fetchCaviarShopRanks(placeId: number): Promise<ShopRankDto[]> {
        return http.get('/api/shop/caviar/rank/v2/list/' + String(placeId))
            .then(r => r.data)
            .then(ranksObj => {
                let res: ShopRankDto[] = [];
                for (let obj of ranksObj) {
                    let sc = Object.assign(new ShopRankDto(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchOblivionToken(): Promise<OblivionTokenDto> {
        return http.get('/api/shop/oblivion/token/info')
            .then(r => r.data)
            .then(oblivionToken => {
                return Object.assign(new OblivionTokenDto(), oblivionToken);
            });
    }

    public static fetchOblivionHero(): Promise<OblivionHeroDto> {
        return http.get('/api/shop/oblivion/hero/info')
            .then(r => r.data)
            .then(oblivionHero => {
                return Object.assign(new OblivionHeroDto(), oblivionHero);
            });
    }

    public static sellInArk(id: number): Promise<Player> {
        return http.get('/api/shop/oblivion/sell/' + id)
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static fetchMonastery10(): Promise<Monastery10Dto> {
        return http.get('/api/shop/monastery/rank/10')
            .then(r => r.data)
            .then(monHero => {
                return Object.assign(new Monastery10Dto(), monHero);
            });
    }

    public static fetchMonastery(tabId: string): Promise<MonasteryDto> {
        return http.get('/api/shop/monastery/' + tabId + '/info')
            .then(r => r.data)
            .then(monHero => {
                return Object.assign(new MonasteryDto(), monHero);
            });
    }

    public static fetchExtendedPortal(): Promise<PortalShopDto> {
        return http.get('/api/shop/creature/portal/extended/list')
            .then(r => r.data)
            .then(obj => {
                return Object.assign(new PortalShopDto(), obj);
            });
    }

    public static fetchExtendedMonastery(): Promise<MonasteryDto> {
        return http.get('/api/shop/extended/monastery')
            .then(r => r.data)
            .then(monHero => {
                return Object.assign(new MonasteryDto(), monHero);
            });
    }

    public static fetchCaviarShopElephs(): Promise<ShopElephant[]> {
        return http.get('/api/shop/caviar/eleph/list')
            .then(r => r.data)
            .then(elephsObj => {
                let res: ShopElephant[] = [];
                for (let obj of elephsObj) {
                    let sc = Object.assign(new ShopElephant(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static fetchCaviarShopMouses(): Promise<ShopElephant[]> {
        return http.get('/api/shop/caviar/mouse/list')
            .then(r => r.data)
            .then(elephsObj => {
                let res: ShopElephant[] = [];
                for (let obj of elephsObj) {
                    let sc = Object.assign(new ShopElephant(), obj);
                    res.push(sc);
                }
                return res;
            });
    }

    public static buyCaviarShopRank(id: number): Promise<Player> {
        return http.post('/api/shop/caviar/rank/' + String(id) + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                GameState.REQUEST_REFRESH_NAV_PANEL = true;
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCaviarShopEleph(id: string): Promise<Player> {
        return http.post('/api/shop/caviar/eleph/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    static fetchMarketInfo(marketName?: string): Promise<MarketInfo> {
        return http.get('/api/shop/market/info', {params: {marketName}}).then((response: any) => {
            return response.data;
        });
    }

    public static buyMarkerItem(id: string, marketName?: string): Promise<Player> {
        return http.post('/api/shop/marker/' + id + '/buy', null, {params: {marketName}})
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyConsumable(id: string, marketName?: string): Promise<Player> {
        return http.post('/api/shop/consumable/' + id + '/buy', null, {params: {marketName}})
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCard(id: string, marketName?: string): Promise<Player> {
        return http.post('/api/shop/card/' + id + '/buy', null, {params: {marketName}})
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }

    public static buyCaviarShopMouse(id: string): Promise<Player> {
        return http.post('/api/shop/caviar/mouse/' + id + '/buy')
            .then(r => r.data)
            .then(playerObj => {
                let player: Player = Object.assign(new Player(), playerObj);
                PlayerStore.SET_PLAYER(player);
                return player;
            });
    }
}
